import React, { useEffect, useState, useRef, useCallback } from "react";
import Search from "./Search";
import { Link } from "react-router-dom";

const CourseListUTM = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState(["All"]);
  const [selectedDepartments, setSelectedDepartments] = useState(["All"]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortByReviewCount, setSortByReviewCount] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
  const departmentDropdownRef = useRef(null);
  const [deptSearch, setDeptSearch] = useState("");

  const fetchCourses = useCallback(async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/courses?faculty=UTM`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
  
      const data = await response.json();
      const rawCourses = data.courses || [];
  
      // Normalise each course record
         const processed = rawCourses.map((course) => {
      const code   = course.code?.trim() || "N/A";
      const prefix = code.slice(0, 3).toUpperCase();   // 3‑letter dept key

      return {
        ...course,
        prefix,                        // ← keep for filtering
        code,
        review_count: course.review_count || 0,
        title: course.title?.trim() || "Untitled Course",
      };
    });

    setCourses(processed);
  
    // Build *distinct* 3‑letter department codes
    const depts = [...new Set(processed.map(({ prefix }) => prefix))].sort();
    setAvailableDepartments(["All", ...depts]);
  } catch (err) {
    console.error("Failed to fetch courses:", err);
    setError(`Failed to load courses: ${err.message}`);
    setCourses([]);
    setAvailableDepartments(["All"]);      // safe fallback
  }
}, [BASE_URL]);

  /**
   * Load data on mount
   */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      await fetchCourses();

      setSelectedDepartments(["All"]);
      setLoading(false);
    };

    loadData();
  }, [fetchCourses]);

  /**
   * Called by <Search> whenever the user types
   */
  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  /**
   * Filter + sort whenever courses, searchTerm, or dept selection changes
   */
  useEffect(() => {
    let results = [...courses];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      results = results.filter(
        (course) =>
          course.code.toLowerCase().includes(searchLower) ||
          course.title.toLowerCase().includes(searchLower)
      );
    }

    if (!selectedDepartments.includes("All")) {
      results = results.filter((c) => selectedDepartments.includes(c.prefix));
    }

    if (sortByReviewCount) {
      results.sort((a, b) => b.review_count - a.review_count || a.code.localeCompare(b.code));
    } else {
      results.sort((a, b) => a.code.localeCompare(b.code));
    }

    setFilteredCourses(results);
  }, [courses, searchTerm, selectedDepartments, sortByReviewCount]);

  /**
   * Toggle the checkboxes for departments
   */
  const handleDepartmentChange = (e) => {
    const { value, checked } = e.target;
    setSelectedDepartments((prev) => {
      if (value === "All") {
        return checked ? ["All"] : [];
      }
      const newSelection = checked
        ? [...prev.filter((d) => d !== "All"), value]
        : prev.filter((d) => d !== value);
      return newSelection.length > 0 ? newSelection : ["All"];
    });
  };

  /**
   * Department dropdown toggler
   */
  const toggleDepartmentDropdown = useCallback(() => {
    setIsDepartmentDropdownOpen((prev) => !prev);
  }, []);

  /**
   * Close the department dropdown when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        departmentDropdownRef.current &&
        !departmentDropdownRef.current.contains(e.target)
      ) {
        setIsDepartmentDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Display text for department button
   */
  const getDepartmentButtonText = () => {
    if (selectedDepartments.includes("All")) return "All Departments";
    if (selectedDepartments.length === 1) return selectedDepartments[0];
    return `${selectedDepartments.length} Departments Selected`;
  };

  /**
   * Render
   */
  if (loading) return <div className="loading">Loading courses...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="course-list">
      <h2>Explore UTM Course Reviews</h2>

      {/* Pass handleSearch to <Search> as onSearch */}
      <Search onSearch={handleSearch} />

      <div className="filters_and_sort">
        {/* Department Filter Dropdown */}
        <div className="dropdown-filters" ref={departmentDropdownRef}>
          <label htmlFor="department-button">Filter by Department:</label>

          <div
            className={`custom-multi-select ${
              isDepartmentDropdownOpen ? "open" : ""
            }`}
          >
            {/* button that opens/closes the menu */}
            <button
              type="button"
              id="department-button"
              className="custom-select-button"
              onClick={toggleDepartmentDropdown}
              aria-haspopup="listbox"
              aria-expanded={isDepartmentDropdownOpen}
              disabled={availableDepartments.length <= 1}
            >
              <span>{getDepartmentButtonText()}</span>
              <span className="custom-arrow"></span>
            </button>

            {/* dropdown contents */}
            {isDepartmentDropdownOpen && (
              <div
                className="custom-select-options"
                role="listbox"
                aria-multiselectable="true"
              >
                {/* ---- new search box ---- */}
                <input
                  type="text"
                  className="dept-search-input"
                  placeholder="Search dept…"
                  value={deptSearch}
                  onChange={(e) => setDeptSearch(e.target.value)}
                  autoFocus
                />

                <ul>
                  {availableDepartments
                    .filter((d) =>
                      d === "All"
                        ? true // always show “All”
                        : d.toLowerCase().includes(deptSearch.toLowerCase())
                    )
                    .map((dept) => (
                      <li key={dept}>
                        <label>
                          <input
                            type="checkbox"
                            value={dept}
                            checked={selectedDepartments.includes(dept)}
                            onChange={handleDepartmentChange}
                          />
                          {dept}
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Sort Controls */}
        <div className="sort">
          Sort by{" "}
          <button
            className={`sort-button ${!sortByReviewCount ? "active-button" : ""}`}
            onClick={() => setSortByReviewCount(false)}
            aria-pressed={!sortByReviewCount}
          >
            Course Code
          </button>
          <span> | </span>
          <button
            className={`sort-button ${sortByReviewCount ? "active-button" : ""}`}
            onClick={() => setSortByReviewCount(true)}
            aria-pressed={sortByReviewCount}
          >
            Most Popular
          </button>
        </div>
      </div>

      {/* Course Display */}
      {filteredCourses.length === 0 ? (
        <p className="no-results-message">No courses match your current filters.</p>
      ) : (
        <div className="courses">
          {filteredCourses.map((course) => (
            <div className="course-card" key={course.id || course.code}>
              <h3>
                <Link to={`/courses/${course.code}`}>
                  {course.code}: {course.title}
                </Link>
              </h3>
              <h2 className="review-count">Reviews: {course.review_count ?? 0}</h2>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CourseListUTM;
